import { CopyButton } from '@heidi/design-system/copy-button';
import { toast } from '@heidi/design-system/toast';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { PencilLine } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import {
  USER_DISCARDED_DRAFT_REASON,
  resolveDraftPresentation,
  type DraftPresentation,
} from '../../contracts/approval-kinds';
import type { ApprovalVerdict } from '../../contracts/harness.types';
import { sharedAgentMessages } from '../../shared.messages';
import { formatDraftHeader, resolveDraftHeaderSpec } from './draft-header';
import { InlineApprovalActions } from './inline-approval-actions';
import { useDraftPreview } from './use-draft-preview';

export type ResolveDraftApproval = (
  approvalId: string,
  verdict: ApprovalVerdict,
  reason?: string,
  modifiedInput?: Record<string, unknown> | null
) => Promise<void>;

export interface DraftApprovalBodyRenderArgs {
  body: string;
  isStreaming: boolean;
  comparison?: {
    before: string;
    after: string;
  };
}

export interface DraftApprovalBlockProps {
  toolName: string;
  toolInput: Record<string, unknown>;
  partialInput?: string;
  status: 'streaming_input' | 'pending_approval';
  approvalId?: string;
  draft?: DraftPresentation | null;
  beforeText?: string;
  onResolve?: ResolveDraftApproval;
  /** Host-supplied note preview (TipTap). Falls back to preformatted text. */
  renderBody?: (args: DraftApprovalBodyRenderArgs) => ReactNode;
}

export const DraftApprovalBlock = ({
  toolName,
  toolInput,
  partialInput,
  status,
  approvalId,
  draft: draftProp,
  beforeText,
  onResolve,
  renderBody,
}: DraftApprovalBlockProps) => {
  const intl = useIntl();
  const draft = draftProp ?? resolveDraftPresentation(toolName);
  const isStreaming = status === 'streaming_input';
  const [pendingVerdict, setPendingVerdict] = useState<ApprovalVerdict | null>(null);

  const { displayBody, unmatched, comparison, copyText } = useDraftPreview({
    toolInput,
    partialInput,
    draft,
    beforeText,
  });

  const canAct = !isStreaming && Boolean(approvalId) && Boolean(onResolve);
  const isBusy = pendingVerdict !== null;
  const submit = (verdict: ApprovalVerdict) => {
    if (!approvalId || !onResolve || isBusy) return;
    setPendingVerdict(verdict);
    const reason = verdict === 'deny' ? USER_DISCARDED_DRAFT_REASON : undefined;
    // In-card rewrite is parked. Approve commits the original tool input.
    // `modified_input` stays on the resolve type if we bring edit back.
    const payload = verdict === 'approve' ? undefined : null;
    void onResolve(approvalId, verdict, reason, payload).catch((error: unknown) => {
      setPendingVerdict(null);
      toast({
        title: intl.formatMessage(sharedAgentMessages.couldntResolve),
        description:
          error instanceof Error
            ? error.message
            : intl.formatMessage({
                defaultMessage: 'Unknown error',
                id: '7POY7QQrzT',
                description: 'Toast fallback when an approval fails without details',
              }),
        tone: 'destructive',
      });
    });
  };

  const handleCopy = async () => {
    const textToCopy = copyText;
    if (!textToCopy) return false;
    try {
      await navigator.clipboard.writeText(textToCopy);
      return true;
    } catch {
      toast({
        title: intl.formatMessage({
          defaultMessage: "Couldn't copy",
          id: '7Z43e8o/yX',
        }),
        tone: 'destructive',
      });
      return false;
    }
  };

  const headerLabel = formatDraftHeader(
    intl,
    resolveDraftHeaderSpec(status, draft, toolInput)
  );

  return (
    <div
      className="my-4 rounded-lg border border-border bg-surface-accent px-4 py-3 first:mt-0"
      data-testid="draft-approval-block"
    >
      <div className="mb-3 flex items-center gap-1.5">
        <PencilLine className="size-3 text-text-tertiary" />
        <TypographyCaption className="text-text-tertiary">
          {headerLabel}
        </TypographyCaption>
      </div>

      {unmatched.length > 0 ? (
        <TypographyCaption className="mb-2 text-text-secondary">
          {intl.formatMessage(
            {
              defaultMessage:
                '{count, plural, one {# find/replace key was not found} other {# find/replace keys were not found}}.',
              id: 'TJ3pDyuACw',
            },
            { count: unmatched.length }
          )}
        </TypographyCaption>
      ) : null}

      <div data-testid="draft-approval-body">
        {renderBody ? (
          renderBody({ body: displayBody, isStreaming, comparison })
        ) : (
          <TypographyP2 className="whitespace-pre-wrap text-pretty text-text-primary">
            {displayBody}
          </TypographyP2>
        )}
      </div>

      {canAct ? (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
          <CopyButton
            onCopy={handleCopy}
            variant="ghost"
            size="sm"
            className="text-text-secondary"
            label={intl.formatMessage({ defaultMessage: 'Copy', id: '4l6vz1/eZ5' })}
            copiedLabel={intl.formatMessage(sharedAgentMessages.copied)}
            data-testid="draft-approval-copy"
          />
          <InlineApprovalActions
            onApprove={() => submit('approve')}
            onDeny={() => submit('deny')}
            pendingVerdict={pendingVerdict}
            denyLabel={intl.formatMessage({
              defaultMessage: 'Deny',
              id: 'htvX+Z11l+',
            })}
            approveTestId="draft-approval-apply"
            denyTestId="draft-approval-discard"
          />
        </div>
      ) : null}
    </div>
  );
};
